/**
 * The scrolling message list for one open thread (#1069).
 *
 * A `FlatList` since Story 1069-1.2 (AC 35, NFR3): a conversation of several
 * hundred messages loads newest-first in pages and scrolls without freezing on
 * a phone. Reading order is oldest → newest (FR66), so "older" is at the TOP:
 *
 *  · `onStartReached` asks for the previous page as the reader scrolls up, and
 *    `maintainVisibleContentPosition` keeps the row under their thumb where it
 *    is while the page is prepended above it (native only; see below).
 *  · A visible "Load earlier messages" control sits at the top while more
 *    exists. It is kept deliberately, not as a fallback: it is the accessible
 *    path, and on web it is the one that keeps the reader's place. Both call
 *    the same `onLoadOlder`.
 *
 * ⚠️ THE SCROLL TRIGGER IS ARMED BY A SCROLL EVENT AND DISARMED WHEN IT FIRES
 * (review, group 3). `VirtualizedList` calls `onStartReached` on the FIRST
 * layout, because the list mounts at offset 0 and 0 is within any threshold —
 * React Native and react-native-web 0.21.2 alike. Left bare, every thread with
 * a second page fetched it before the reader did anything. Worse on web: RNW
 * 0.21.2 has no `maintainVisibleContentPosition`, so after a prepend the
 * offset is STILL 0, the content length has changed, and the trigger fires
 * again — a 300-message thread loaded itself to the beginning in fifteen
 * requests. Requiring a scroll event between fires stops both: nothing has
 * scrolled at mount, and a prepend at offset 0 emits no scroll event. On
 * native the prepend moves the offset away from the top, so the next fire
 * needs the reader to scroll back up, which is the intended gesture.
 *
 * SINCE #1069 STORY 7.6 A FILE LEADS BACK TO ITS MESSAGE (FR79). The panel
 * hands a `reveal` prop and the list performs the jump against its own ref:
 * `scrollToIndex` to the row's reading-order index, centred, and a mark on
 * the bubble for `MESSAGE_MARK_MS`. What it does NOT touch: `followEnd` is
 * never written by a jump, the opening window is respected (a reveal that
 * arrives inside it is queued and run by `settleOpening`), and the older-page
 * trigger's arming rule above is unchanged -- a programmatic scroll arms it
 * exactly as a manual one does. See the jump's own comment in the body.
 *
 * The list owns the scroller and the empty/loading affordance. The composer
 * stays with the panel, because it is not part of the thread.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { LIST_PADDING_H } from './bubbleGeometry';
import { sameLocalDay } from './format';
import {
  BUBBLE_RADIUS,
  GROUP_AUTHOR_OWN,
  MESSAGE_MARK_MS,
  MessageRow,
} from './MessageRow';
import { DEFAULT_PAGE_LIMIT } from './providers/provider.types';
import { useReduceMotion } from './useReduceMotion';
import { storage } from '../../services/storage';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight, lineHeight } from '../../theme/typography';

import type { Message, PendingMessage } from './correspondence.types';
import type { JumpReveal } from './useJumpToMessage';
import type { LayoutChangeEvent, ListRenderItem } from 'react-native';

/**
 * Re-exported from the row, which owns the animation (Story 7.6, D5): the
 * list clears the mark after this long, and the tests find that timer by
 * this delay beside `MESSAGE_LIST_REVEAL_TIMEOUT_MS` (C10). It lives in
 * `MessageRow.tsx` because this file imports that one.
 */
export { MESSAGE_MARK_MS };

/** Rows rendered before virtualization takes over: one server page. */
export const MESSAGE_LIST_INITIAL_RENDER = DEFAULT_PAGE_LIMIT;

/** The control's copy. Exported so tests match it rather than retype it. */
export const LOAD_OLDER_LABEL = 'Load earlier messages';

/**
 * How long a thread is treated as still OPENING (Story 5.5, AC 21): the
 * window in which the list re-applies its opening scroll to each batch of
 * rows that lays out, and the deadline after which it shows itself whether or
 * not it was ever positioned. Long enough that a real layout pass always wins
 * (a frame or two), short enough that a starved one — a tab that is not being
 * rendered — is a blink rather than a blank panel.
 */
export const MESSAGE_LIST_REVEAL_TIMEOUT_MS = 250;

/**
 * How long the list waits for the content size to stop changing before it
 * shows itself (Story 5.5, AC 21). A virtualized list reports its size more
 * than once while a thread opens — `initialNumToRender` cells first and the
 * rest a batching period later, and again when a web font or a re-measured
 * row changes a height — so a list revealed on the FIRST report is visible
 * for the jumps that follow, which is the flash this window exists to
 * remove (owner, 2026-09-11: "I still see some flash as it loads on web,
 * not native"). Three frames at 60Hz: long enough to swallow the next
 * report, short enough to be no perceptible delay. `MESSAGE_LIST_REVEAL_
 * TIMEOUT_MS` is the ceiling on the whole wait.
 */
export const MESSAGE_LIST_SETTLE_MS = 48;

/**
 * Per-device flag: the one-time "press and hold" hint has been seen (Story
 * 2.5 sibling, D-i). A boolean in the house storage wrapper (AsyncStorage on
 * native); nothing sensitive. Set by the first long press anywhere in a list
 * or by the hint's dismiss, and never unset.
 */
export const LONG_PRESS_HINT_STORAGE_KEY =
  'correspondence_long_press_hint_seen';

/**
 * The hint's three states. `'unknown'` until the storage read resolves, and
 * hidden while unknown, so a reader who learned the gesture last week does
 * not see it flash for a frame.
 */
type HintState = 'unknown' | 'show' | 'hidden';

/**
 * The hint's session latch, module-wide: once any list in this app session
 * has retired the hint, no later mount shows it, whether or not the storage
 * write has landed (a thread closed and reopened within the write's flight
 * time) or ever lands (a device where the write keeps failing). The flag in
 * storage is what survives a relaunch; this is what survives a remount.
 */
let hintRetiredThisSession = false;

/** Test-only: the latch above is module state and would leak across cases. */
export const resetLongPressHintSessionForTests = (): void => {
  hintRetiredThisSession = false;
};

/**
 * Owns the one-time hint: whether it may show, and its retirement. In the
 * LIST rather than the row because "the first eligible message" is a property
 * of the list and "retired by any long press" spans rows. Native only.
 */
const useLongPressHint = (native: boolean) => {
  const [state, setState] = useState<HintState>(() =>
    hintRetiredThisSession ? 'hidden' : 'unknown',
  );
  // What the read said, so a retire on a device that already has the flag
  // does not write it again (and a retire before the read resolves does).
  const storedSeen = useRef(false);

  useEffect(() => {
    if (!native || hintRetiredThisSession) return undefined;
    let cancelled = false;
    // `Promise.resolve().then(...)` so a wrapper that throws or returns a bare
    // value (the harness after `resetMocks`) lands in the same branch.
    Promise.resolve()
      .then(() => storage.getItem(LONG_PRESS_HINT_STORAGE_KEY))
      .then(
        value => {
          if (cancelled || hintRetiredThisSession) return;
          storedSeen.current = !!value;
          setState(value ? 'hidden' : 'show');
        },
        () => {
          // A failed read is treated as unset: the hint shows.
          if (!cancelled && !hintRetiredThisSession) setState('show');
        },
      );
    return () => {
      cancelled = true;
    };
  }, [native]);

  const retire = useCallback(() => {
    if (hintRetiredThisSession) return;
    hintRetiredThisSession = true;
    setState('hidden');
    if (storedSeen.current) return; // the device already knows
    // Fire and forget: a failed write costs a repeat of the hint on the next
    // launch (never on the next mount: the latch holds), and never an error
    // in the reader's face. Logged without payload.
    Promise.resolve()
      .then(() => storage.setItem(LONG_PRESS_HINT_STORAGE_KEY, '1'))
      .catch(() => {
        if (__DEV__) {
          console.warn('correspondence: long-press hint flag not saved');
        }
      });
  }, []);

  return { show: state === 'show', retire };
};

interface Props {
  messages: Message[];
  /**
   * The viewer's `users.user_id` as a string. Optional because
   * `viewerFromUser` returns null when there is no signed-in email — in which
   * case NOTHING is "mine" and the whole thread renders on the left, which is
   * the safe reading.
   */
  viewerId?: string;
  /**
   * #1599 Story 1.7: the viewer holds Configuration Engineer (decided by the
   * panel), so every bubble tints the team token as theirs. The pending rows
   * render no body (Story 1.3, D3) and take nothing.
   */
  viewerIsConfigTeam?: boolean;
  /** The viewer's own name, for `MessageRow`'s own-row fallback. */
  viewerName?: string;
  canSeeInternal?: boolean;
  loading: boolean;
  /** More history exists above the first row on screen (a cursor is held). */
  hasOlder?: boolean;
  /** An older page is being fetched. */
  loadingOlder?: boolean;
  /** Fetch and prepend the older page. Both the control and the scroll call it. */
  onLoadOlder?: () => void;
  /**
   * The viewer's own posts awaiting confirmation (Story 1.3, AC 17), rendered
   * AFTER the data rows as own-side rows through `ListFooterComponent`, so the
   * list's data, keys and scroll rules are untouched.
   */
  pending?: PendingMessage[];
  /**
   * Call off an in-flight post (#1069 Story 4.1, AC 11, FR18). Absent means no
   * `Cancel` renders, which is how every caller that predates files behaves.
   */
  onCancelPending?: (localId: string) => void;
  /** Row actions (Story 1.3, AC 35); see `MessageRow`. */
  onEdit?: (message: Message) => void;
  onDelete?: (message: Message) => void;
  /**
   * Open one attachment (Story 4.2, AC 12). Passed straight through: the list
   * owns neither the viewers nor the thread id, and the panel owns both.
   */
  onOpenAttachment?: (
    message: Message,
    index: number,
    kind: 'image' | 'document',
  ) => void;
  /** Save one attached file (Story 4.2). */
  onDownloadAttachment?: (message: Message, fileId: string) => void;
  mayDeleteAny?: boolean;
  /**
   * The message in the composer, if any (Story 2.5, AC 10): its row shows
   * the trigger with Edit disabled rather than hidden.
   */
  editingMessageId?: string | null;
  /**
   * A jump to perform (Story 7.6, D4): scroll this message to the middle of
   * the viewport and mark its bubble. A PROP, not a ref handle: the list has
   * no imperative surface and the settle window is its private state (C4).
   * The `nonce` is what lets the same message be chosen twice and what keys
   * the effect, so a re-render never re-scrolls. The hook guarantees the id
   * is in `messages`; the list does not trust it and does nothing otherwise.
   */
  reveal?: JumpReveal | null;
}

/**
 * A pending row: the viewer's side, muted, "Sending…" and NOT the body
 * (Story 1.3 code review, D3). The draft stays in the composer until the
 * server confirms (it may hold a pasted email, and clearing first was data
 * loss), so echoing it here put the same text on screen twice for the whole
 * round trip. One copy: the composer's. The row is the place-holder for
 * where the message will land; `item.body` is kept on the type for the
 * failure path's recovery.
 */
/** AC 10's third line, verbatim. */
export const PENDING_SLOW_LINE =
  'Still sending. Large files can take a while on a slow connection.';

const PendingRow: React.FC<{
  item: PendingMessage;
  showAuthor: boolean;
  onCancel?: (localId: string) => void;
}> = ({ item, showAuthor, onCancel }) => {
  // ⚠️ THE KEY, NOT THE COUNT (#1069 Story 4.1, AC 8). A text-only pending row
  // is untouched by this story, so everything below branches on `fileCount`
  // being PRESENT rather than on it being greater than zero.
  const carriesFiles = item.fileCount !== undefined;
  return (
    <View
      style={styles.pendingRow}
      testID={`correspondence-message-pending-${item.localId}`}
    >
      <View style={styles.pendingSpacer} />
      <View style={styles.pendingColumn}>
        {/* The own-side GROUP shape (Story 5.5, C4): `You` above, the own
            radii, no avatar; the timestamp line is the pending status. */}
        {showAuthor ? (
          <Text style={styles.pendingAuthor}>{GROUP_AUTHOR_OWN}</Text>
        ) : null}
        <View style={styles.pendingBubble}>
          <Text style={styles.pendingStatus}>Sending…</Text>

          {carriesFiles ? (
            <View style={styles.pendingProgress}>
              {item.percent === null ? (
                // ⚠️ THE TRANSPORT CANNOT MEASURE THIS ONE (AC 9). A spinner,
                // never a bar at an invented width: "Never animate a fake bar".
                <ActivityIndicator
                  size={12}
                  color={colors.info}
                  testID="correspondence-pending-indeterminate"
                />
              ) : (
                <View style={styles.pendingProgressTrack}>
                  <View
                    testID="correspondence-pending-progress-fill"
                    style={[
                      styles.pendingProgressFill,
                      { width: `${item.percent ?? 0}%` },
                    ]}
                  />
                </View>
              )}
              <Text style={styles.pendingCaption}>
                {`${item.fileCount} ${item.fileCount === 1 ? 'file' : 'files'}${
                  item.percent === null ? '' : ` · ${item.percent ?? 0}%`
                }`}
              </Text>
            </View>
          ) : null}

          {carriesFiles && item.slow ? (
            <Text style={styles.pendingCaption}>{PENDING_SLOW_LINE}</Text>
          ) : null}

          {/* ⚠️ GATED ON `carriesFiles` TOO (code review 2026-09-13). This was
              `onCancel ? …`, and the panel passes `onCancelPending`
              unconditionally, so every text-only send grew a Cancel link it
              never had — and an inert one: an abort handle is only registered
              on the files branch, so pressing it was `undefined?.()`. AC 8 says
              a text-only pending row is untouched, and AC 11 scopes Cancel to
              the file case. Every other conditional in this row branches on
              `fileCount !== undefined`; this one did not. */}
          {onCancel && carriesFiles ? (
            <TouchableOpacity
              onPress={() => onCancel(item.localId)}
              accessibilityRole="button"
              accessibilityLabel="Cancel sending"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={styles.pendingCancel}>Cancel</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  );
};

/**
 * Does the pending BLOCK open with `You` (Story 5.5, C4 as amended at code
 * review, 2026-09-11)? C4 was written before grouping existed to compare it
 * against, so taken literally it put a `You` above EVERY pending row: post
 * twice in five seconds and the thread read `You` / two bubbles / timestamp
 * / `You`, with a third for a second queued send. That is the duplication
 * this story exists to remove, in the one place the reader is watching
 * hardest.
 *
 * D4 applied to the footer, and nothing more: the label is the pending
 * BLOCK's, not each row's, and it is suppressed when the last live message
 * already opens an own-side group the pending rows continue — same author,
 * same local day, inside `GROUP_WINDOW_MS`, and not a tombstone. `now` is a
 * parameter so the window is testable.
 *
 * Deliberately NOT solved by moving pending rows into `data`: AC 12 pins
 * `data` and `keyExtractor` by reference, and a `PendingMessage` has a
 * `localId`, not an `id`.
 */
export const pendingBlockShowsAuthor = (
  messages: Message[],
  viewerId: string | undefined,
  now: Date = new Date(),
): boolean => {
  const last = messages[messages.length - 1];
  if (!last || last.deleted) return true;
  if (!viewerId || last.authorId !== viewerId) return true;
  const sent = new Date(last.sentAt).getTime();
  if (Number.isNaN(sent)) return true;
  if (Math.abs(now.getTime() - sent) > GROUP_WINDOW_MS) return true;
  return !sameLocalDay(last.sentAt, now.toISOString());
};

/** Exported so a test can pin the list's key by REFERENCE (Story 5.5, AC 12). */
export const messageKeyExtractor = (m: Message): string => m.id;

/**
 * How far apart two consecutive messages by one author may be and still read
 * as one group (Story 5.5, decision D4): five minutes, inclusive.
 */
export const GROUP_WINDOW_MS = 5 * 60 * 1000;

/**
 * Whether `item` continues the group `prev` is in (D4): both LIVE, the same
 * `authorId`, on the same local calendar day, and within `GROUP_WINDOW_MS`
 * of each other. A tombstone never starts, joins or continues a group, so a
 * live message after one starts afresh. Edit state plays no part. The day
 * test is not in the brief; it is here because a divider inside a group
 * (23:58 then 00:01, same author) would say "a new day" and "the same
 * breath" at once.
 */
export const continuesGroup = (prev: Message, item: Message): boolean => {
  if (prev.deleted || item.deleted) return false;
  if (prev.authorId !== item.authorId) return false;
  // #1599 Story 4.1: the Config Team label is stored PER MESSAGE and only the
  // group head draws it, so a change of label starts a new group rather than
  // letting the head speak for messages it does not describe.
  if ((prev.authorConfigTeam === true) !== (item.authorConfigTeam === true))
    return false;
  const a = new Date(prev.sentAt).getTime();
  const b = new Date(item.sentAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  if (Math.abs(b - a) > GROUP_WINDOW_MS) return false;
  return sameLocalDay(prev.sentAt, item.sentAt);
};

export interface GroupingAt {
  /**
   * This message renders the group's author label (and badge) above it and,
   * on the other side, the avatar beside its bubble (since 2026-09-23).
   */
  startsGroup: boolean;
  /**
   * This message is the last of its group. Since 2026-10-05 the row reads
   * nothing from it (the timestamp moved onto the author line, with
   * `startsGroup`); it is kept on the grouping result for the pins and for
   * any consumer that needs the group's end.
   */
  endsGroup: boolean;
  /** The day divider renders inside this item, above its label (D5). */
  showDayDivider: boolean;
  /**
   * The LATEST `editedAt` in the whole group, carried to the message that
   * STARTS it (D2 as moved 2026-10-05: one " · edited" per group, on the
   * header timestamp). Undefined when nothing in the group was edited, or on
   * a message that does not start its group.
   */
  groupEditedAt?: string;
}

/**
 * Grouping and the day divider for the item at `index`, from its neighbours
 * and nothing else (Story 5.5, C7, D4, D5). A pure function of the array so
 * a prepend or a refetch regroups on the next render for free; nothing is
 * cached (Story 2.3's live refresh relies on that).
 */
export const groupingAt = (messages: Message[], index: number): GroupingAt => {
  const item = messages[index];
  const prev = index > 0 ? messages[index - 1] : undefined;
  const next = messages[index + 1];
  const startsGroup = !prev || !continuesGroup(prev, item);
  const endsGroup = !next || !continuesGroup(item, next);
  const showDayDivider = !prev || !sameLocalDay(prev.sentAt, item.sentAt);
  let groupEditedAt: string | undefined;
  // On the message that STARTS the group since 2026-10-05 (owner: the
  // timestamp sits on the author line, as Teams' header does; it carried
  // the edit on the LAST message under Story 5.5 D2). Walk FORWARD over the
  // group and keep the latest edit.
  if (startsGroup && !item.deleted) {
    let latest = -Infinity;
    for (let j = index; j < messages.length; j += 1) {
      const m = messages[j];
      if (m.editedAt) {
        const t = new Date(m.editedAt).getTime();
        if (t > latest) {
          latest = t;
          groupEditedAt = m.editedAt;
        }
      }
      if (j === messages.length - 1 || !continuesGroup(m, messages[j + 1])) {
        break;
      }
    }
  }
  return { startsGroup, endsGroup, showDayDivider, groupEditedAt };
};

export const MessageList: React.FC<Props> = ({
  messages,
  viewerId,
  viewerIsConfigTeam = false,
  viewerName,
  canSeeInternal = false,
  loading,
  hasOlder = false,
  loadingOlder = false,
  onLoadOlder,
  pending = [],
  onCancelPending,
  onEdit,
  onDelete,
  onOpenAttachment,
  onDownloadAttachment,
  mayDeleteAny = false,
  editingMessageId = null,
  reveal: revealRequest = null,
}) => {
  // Per render (the tests flip it), and the hint is a native affair: web has
  // a visible "…" and needs no teaching.
  const native = Platform.OS !== 'web';
  // One read, shared with the Dock and the row (Story 7.6, D11): an instant
  // scroll and a static mark for the reader who asked for less motion.
  const reduceMotion = useReduceMotion();
  /**
   * The row a jump landed on, while its mark is drawn (Story 7.6, D5). Declared
   * here, above `renderItem`, which reads it; the jump itself is below, with
   * the opening window it has to respect.
   */
  /**
   * The marked row and the reveal it belongs to. The nonce is what remounts
   * the overlay when the SAME row is chosen again inside the mark window, so
   * the second mark starts from full strength instead of continuing a fade
   * that was almost done (review 2026-09-19).
   */
  const [marked, setMarked] = useState<{ id: string; nonce: number } | null>(
    null,
  );
  const { show: hintAvailable, retire: retireHint } = useLongPressHint(native);

  /**
   * The row the hint goes under: the FIRST message the viewer could act on,
   * by the same rule the row applies (own live message with a handler, or
   * any live message for an administrator with a delete handler). `null`
   * when there is none, so an empty thread or a customer's read-only view
   * shows nothing.
   */
  // Latched once chosen: "Load earlier messages" prepends older rows, and a
  // hint that jumped to the new first eligible row would teleport off the top
  // of the screen. It stays where it was shown while that row is still live.
  const hintedId = useRef<string | null>(null);
  const hint = useMemo(() => {
    if (!native || !hintAvailable) return null;
    /**
     * The ROW's rule, not a paraphrase of it (code review, 2026-09-10). This
     * used to answer `'own'` whenever `onEdit || onDelete` was wired, while
     * `MessageRow` decides Edit from `onEdit` alone and Delete from
     * `onDelete` alone — so with only one handler wired the hint promised
     * "edit or delete" beside a chip offering one verb. Two copies of an
     * authorization rule the story says is decided in exactly one place.
     * Mirrors `MessageRow.tsx`'s `offerEdit` / `offerDelete` exactly.
     */
    const kindOf = (m: Message): 'own' | 'any' | null => {
      if (m.deleted) return null;
      const own = !!viewerId && m.authorId === viewerId;
      const offerEdit = own && !!onEdit;
      const offerDelete = (own || mayDeleteAny) && !!onDelete;
      if (!offerEdit && !offerDelete) return null;
      // "Edit or delete" only when Edit is genuinely on offer.
      return offerEdit ? 'own' : 'any';
    };
    const held = hintedId.current
      ? messages.find(m => m.id === hintedId.current)
      : undefined;
    const heldKind = held ? kindOf(held) : null;
    if (held && heldKind) return { id: held.id, kind: heldKind };
    /**
     * Once the hint has been shown somewhere, it never moves (code review,
     * 2026-09-10). The latch existed so a prepend could not teleport it off
     * the top of the screen, but it only held while the hinted row stayed
     * eligible: withdraw that message and the search below re-latched onto
     * the oldest eligible row, which after any prepend is far above the
     * viewport — the hint silently relocated somewhere the reader will never
     * see it, and so was never retired. If the row it was shown under is
     * gone, the hint has had its chance.
     */
    if (hintedId.current !== null) return null;
    for (const m of messages) {
      const kind = kindOf(m);
      if (kind) {
        hintedId.current = m.id;
        return { id: m.id, kind };
      }
    }
    return null;
  }, [
    native,
    hintAvailable,
    messages,
    viewerId,
    mayDeleteAny,
    onEdit,
    onDelete,
  ]);

  const renderItem: ListRenderItem<Message> = useCallback(
    ({ item, index }) => {
      /**
       * Grouping and the divider from the neighbours, per render (Story 5.5,
       * C7): `renderItem` receives `index`, and `messages` is the prop the
       * list already holds. Nothing is cached, so a prepend, a refetch or a
       * delete regroups on the next render.
       */
      const group = groupingAt(messages, index);
      return (
        <MessageRow
          canSeeInternal={canSeeInternal}
          message={item}
          viewerName={viewerName}
          // #1599 Story 1.5: the bubble marker and the own-token tint.
          viewerId={viewerId}
          // #1599 Story 1.7: the team token's own-token tint.
          viewerIsConfigTeam={viewerIsConfigTeam}
          /**
           * Identity match only. An author id the viewer does not hold can never
           * equal the viewer's id, so it falls to the left — which is the
           * required behaviour, not an accident worth relying on quietly.
           */
          isOwn={!!viewerId && item.authorId === viewerId}
          startsGroup={group.startsGroup}
          showDayDivider={group.showDayDivider}
          groupEditedAt={group.groupEditedAt}
          onEdit={onEdit}
          onDelete={onDelete}
          mayDeleteAny={mayDeleteAny}
          editing={editingMessageId !== null && item.id === editingMessageId}
          longPressHint={hint && hint.id === item.id ? hint.kind : undefined}
          onDismissLongPressHint={retireHint}
          onLongPressUsed={retireHint}
          onOpenAttachment={onOpenAttachment}
          onDownloadAttachment={onDownloadAttachment}
          /* The jump's mark (Story 7.6, D5): fading, or held still for the
             reader who asked for less motion (C3). */
          mark={
            marked !== null && item.id === marked.id
              ? reduceMotion
                ? 'static'
                : 'animated'
              : undefined
          }
          markKey={
            marked !== null && item.id === marked.id ? marked.nonce : undefined
          }
        />
      );
    },
    [
      messages,
      canSeeInternal,
      viewerId,
      viewerIsConfigTeam,
      viewerName,
      onEdit,
      onDelete,
      onOpenAttachment,
      onDownloadAttachment,
      mayDeleteAny,
      editingMessageId,
      hint,
      retireHint,
      marked,
      reduceMotion,
    ],
  );

  const footer =
    pending.length > 0 ? (
      <View style={styles.pendingList} testID="correspondence-pending-list">
        {pending.map((item, index) => (
          <PendingRow
            key={item.localId}
            item={item}
            /* The BLOCK's label, on its first row only. */
            showAuthor={
              index === 0 && pendingBlockShowsAuthor(messages, viewerId)
            }
            onCancel={onCancelPending}
          />
        ))}
      </View>
    ) : null;

  /**
   * THE THREAD OPENS AT ITS NEWEST MESSAGE AND FOLLOWS NEW ONES (Story 5.5,
   * AC 20, owner decision D9). A `FlatList` mounts at offset 0, which in
   * reading order is the OLDEST message; the newest is what a reader opens a
   * thread for. Decided here, from the ids alone, and nowhere else:
   *
   *  · first content (0 → n messages, or a mount with messages): scroll to
   *    the end, not animated — the thread simply opens there;
   *  · the LAST id changed (an own post, the post-then-refetch, Story 2.3's
   *    live refresh, or a newest page that replaced the list): scroll to the
   *    end, animated;
   *  · a pending row appeared (the reader's own send): the same;
   *  · the FIRST id changed with the last unchanged (an older page was
   *    prepended): NEVER scroll — the reader asked for history and
   *    `maintainVisibleContentPosition` keeps their place — and CLEAR any
   *    scroll still waiting for layout, so the prepend's own size change
   *    cannot fire it;
   *  · an edit or a delete changes no id: nothing.
   *
   * ⚠️ NOT `scrollToEnd` (owner, 2026-09-11: "still scrolled to the top").
   * `VirtualizedList.scrollToEnd` computes its offset from the LAST CELL's
   * frame metrics, which are unknown until that cell has reported its
   * layout; called from the effect it computes 0, and called from
   * `onContentSizeChange` it still reads the approximation on react-native-
   * web, so the list stayed at the top. The offset is computed HERE from two
   * numbers the platform hands over exactly when they are true: the
   * viewport height from `onLayout`, the content height from
   * `onContentSizeChange`. `scrollToOffset` then goes straight to the
   * scroller. The flag is consumed by the first size change after it was
   * set; the effect only arms it. Unconditional on where the reader is
   * scrolled, as the owner asked.
   */
  const listRef = useRef<FlatList<Message>>(null);
  const followEnd = useRef<false | 'instant' | 'animated'>(false);
  /**
   * Has the thread been put where it belongs yet (Story 5.5, AC 21, owner
   * decision D13)? Until it has, a list that HAS messages is painted
   * transparent, so the opening scroll happens off-screen and a thread
   * simply appears at its newest message — the way Messages does — instead
   * of appearing at the top and shooting down. It is one frame either way;
   * the difference is whether the reader sees the movement.
   *
   * A list with nothing to position (the spinner, the empty thread) is NEVER
   * hidden: there is no scroll to conceal, and hiding it would blank the one
   * affordance that says the thread is still coming.
   */
  const [positioned, setPositioned] = useState(false);
  const revealTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reveal = useCallback(() => {
    if (revealTimer.current !== null) {
      clearTimeout(revealTimer.current);
      revealTimer.current = null;
    }
    setPositioned(true);
  }, []);
  /**
   * The opening is over: show the thread whether or not it was ever
   * positioned (the fail-safe), and stop re-applying the opening scroll to
   * the batches still arriving. One callback for both, because they are one
   * moment: the thread has finished opening.
   */
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * True from the moment an opening is armed until it settles (code review,
   * 2026-09-11). The opening is a WINDOW, not a single event, and two holes
   * came from having no name for it: an append that landed inside the window
   * re-armed the flag to `'animated'`, which skipped `setPositioned(false)`
   * and revealed the list mid-scroll — the "appears at the top and shoots
   * down" AC 21 exists to remove, on the one path AC 21 did not cover — and
   * a stale `'animated'` survived `settleOpening` to be consumed later by an
   * unrelated size change (a body expanding, a font swapping), yanking a
   * reader out of history. See the two guards below.
   */
  const opening = useRef(false);

  /**
   * THE JUMP (Story 7.6, D4): a file in the strip leads back to its message.
   * The panel hands a `reveal` prop; the list finds the row's index in
   * `messages` (reading order IS the FlatList index: the header and the
   * footer are not rows), calls `scrollToIndex` against its own ref with
   * `viewPosition: 0.5`, and marks the row for `MESSAGE_MARK_MS`.
   *
   * ⚠️ NEVER WRITES `followEnd`, AND NEVER SCROLLS INSIDE THE OPENING WINDOW
   * (C7). A `scrollToIndex` issued while `opening.current` is true would be
   * overwritten by the next size report's `scrollToEnd`, so a reveal that
   * arrives then is QUEUED here and run by `settleOpening`; one that arrives
   * after runs at once. What the jump does to the older-page trigger is
   * accepted, not prevented: a programmatic scroll fires `onScroll` like any
   * other, so a landing within the threshold of the top while more history
   * exists fetches one more page -- exactly what a manual scroll to that spot
   * does (AC 9, the ledger).
   *
   * ⚠️ `onScrollToIndexFailed` IS MANDATORY, NOT A NICETY (C5). There is no
   * `getItemLayout` (rows are variable height), so both platforms invariant
   * on the handler being present when the index is above the highest measured
   * cell. The failure path is the FALLBACK, not the normal path: after older
   * pages are prepended the index numbering shifts under a stale maximum, an
   * unmeasured low index does not fail, and the list lands on the platform's
   * average-length approximation -- which is why the mark is load-bearing
   * and not decoration. On a failure: jump to the estimate, then ONE retry
   * of `scrollToIndex` on the next tick; a second failure is left where the
   * estimate put it, still marked.
   */
  const markTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The reveal a `scrollToIndex` failure may retry ONCE, by nonce. */
  const retriedNonce = useRef<number | null>(null);
  const currentReveal = useRef<JumpReveal | null>(null);
  /** A reveal that arrived inside the opening window, waiting for the settle. */
  const pendingReveal = useRef<JumpReveal | null>(null);
  const performReveal = useCallback(
    (target: JumpReveal) => {
      const index = messages.findIndex(m => m.id === target.messageId);
      if (index < 0) return;
      currentReveal.current = target;
      setMarked({ id: target.messageId, nonce: target.nonce });
      // The mark's clock: the overlay UNMOUNTS when this fires, whether or
      // not its animation finished (D5), so nothing is left behind.
      if (markTimer.current !== null) clearTimeout(markTimer.current);
      markTimer.current = setTimeout(() => {
        markTimer.current = null;
        setMarked(null);
      }, MESSAGE_MARK_MS);
      listRef.current?.scrollToIndex({
        index,
        animated: !reduceMotion,
        viewPosition: 0.5,
      });
    },
    [messages, reduceMotion],
  );
  // Through a ref so `settleOpening` can run a queued reveal without taking
  // `messages` as a dependency (its identity is what every timer captures).
  const performRevealRef = useRef(performReveal);
  useEffect(() => {
    performRevealRef.current = performReveal;
  }, [performReveal]);
  /** The latest list, for the retry: a prepend between the failure and the
   *  retry renumbers every index (review 2026-09-19). */
  const latestMessages = useRef(messages);
  useEffect(() => {
    latestMessages.current = messages;
  }, [messages]);

  const settleOpening = useCallback(() => {
    if (settleTimer.current !== null) {
      clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
    // Whatever was armed, the opening is over and nothing may outlive it:
    // an unconsumed flag here has no size change left that belongs to it.
    followEnd.current = false;
    opening.current = false;
    reveal();
    // A jump that arrived while the thread was opening runs now, on a list
    // that has stopped moving (Story 7.6, C7).
    const queued = pendingReveal.current;
    if (queued !== null) {
      pendingReveal.current = null;
      performRevealRef.current(queued);
    }
  }, [reveal]);
  useEffect(
    () => () => {
      if (revealTimer.current !== null) clearTimeout(revealTimer.current);
      if (settleTimer.current !== null) clearTimeout(settleTimer.current);
      if (markTimer.current !== null) clearTimeout(markTimer.current);
      if (retryTimer.current !== null) clearTimeout(retryTimer.current);
    },
    [],
  );
  const lastSeen = useRef<{
    first?: string;
    last?: string;
    count: number;
    pendingCount: number;
  }>({ count: 0, pendingCount: 0 });
  const viewportHeight = useRef(0);
  const contentHeight = useRef(0);
  const scrollToEnd = useCallback((mode: 'instant' | 'animated') => {
    // The content is at least as tall as the viewport when there is
    // anything to scroll to, so `max(0, …)` only guards the empty list.
    listRef.current?.scrollToOffset({
      offset: Math.max(0, contentHeight.current - viewportHeight.current),
      animated: mode === 'animated',
    });
  }, []);
  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    viewportHeight.current = event.nativeEvent.layout.height;
  }, []);
  // ⚠️ A LAYOUT effect, not a passive one (owner, 2026-09-11, second
  // report). On web the rows' size change arrives from a ResizeObserver
  // right after the commit, BEFORE React runs passive effects; armed from
  // `useEffect` the flag was set after the one event that should have
  // consumed it, and the list stayed at the top. A layout effect runs
  // synchronously inside the commit, before any layout callback on either
  // platform, so the size change that follows always finds the flag set.
  useLayoutEffect(() => {
    const prev = lastSeen.current;
    const first = messages[0]?.id;
    const last = messages[messages.length - 1]?.id;
    const next = {
      first,
      last,
      count: messages.length,
      pendingCount: pending.length,
    };
    lastSeen.current = next;
    let mode: false | 'instant' | 'animated' = false;
    if (messages.length > 0 && prev.count === 0) {
      mode = 'instant';
    } else if (last !== undefined && last !== prev.last) {
      mode = 'animated';
    } else if (pending.length > prev.pendingCount) {
      mode = 'animated';
    } else if (first !== prev.first) {
      // A prepend: keep the reader's place, and forget any pending scroll.
      followEnd.current = false;
      return;
    }
    if (!mode) return;
    /**
     * AN APPEND INSIDE THE OPENING WINDOW IS PART OF THE OPENING, not a
     * follow (code review, 2026-09-11). A message landing before the first
     * size change — the 10s live refresh, or the reader's own post
     * resolving — used to overwrite the flag with `'animated'`, which
     * returns below without arming anything and whose size-change branch
     * calls `reveal()` at once: the list became visible WHILE an animated
     * scroll from offset 0 was still running. Treated as an opening it stays
     * hidden, re-applies the scroll to each batch, and settles once.
     */
    if (mode === 'animated' && opening.current) mode = 'instant';
    followEnd.current = mode;
    if (mode === 'instant') {
      opening.current = true;
      /**
       * ⚠️ BEING SHOWN IS PER-OPENING, NOT ONCE PER MOUNT (owner, 2026-09-11,
       * third report — the web-only flash). The list mounts EMPTY, because
       * the thread's messages are still in flight, and an empty list is never
       * hidden: there is nothing to position and hiding it would blank the
       * spinner. On web that empty list then reports its own content size
       * (a `ResizeObserver` fires for the empty content container) BEFORE the
       * messages land, and that report — finding nothing armed — marked the
       * list shown. The messages then arrived into a list already marked, so
       * the opening scroll ran in full view. Native does not report an empty
       * content size in that window, which is why only web showed it.
       */
      setPositioned(false);
    }
    // Arm only: the rows have not laid out yet, so the content height on
    // hand is the OLD one (or 0). The size change below does the scroll.
    if (mode !== 'instant') return;
    /**
     * ⚠️ THE REVEAL FAILS TOWARDS VISIBLE (AC 21). A browser tab that is not
     * rendering fires no `ResizeObserver` callback at all — measured on this
     * panel on 2026-09-11, where `visibilityState: 'hidden'` starved both the
     * scroll and any reveal that waited on it — and a background tab the
     * reader switches to must not show them a blank panel. If the positioning
     * event has not arrived by `MESSAGE_LIST_REVEAL_TIMEOUT_MS`, the thread is
     * shown where it is: unscrolled and legible beats invisible and correct.
     */
    // Armed ONCE per opening, never re-armed: the ceiling belongs to the
    // opening, and a burst of appends inside the window must not be able to
    // push the fail-safe out indefinitely.
    if (revealTimer.current === null) {
      revealTimer.current = setTimeout(
        settleOpening,
        MESSAGE_LIST_REVEAL_TIMEOUT_MS,
      );
    }
  }, [messages, pending.length, settleOpening]);
  const handleContentSizeChange = useCallback(
    (_width: number, height: number) => {
      contentHeight.current = height;
      const mode = followEnd.current;
      if (!mode) {
        /**
         * Nothing to position — an empty thread, or a size change that is
         * somebody else's. Either way the list has no reason to stay hidden.
         *
         * Code review 2026-09-11 proposed holding the list hidden here while
         * an opening is still in flight with its flag momentarily cleared (a
         * prepend inside the window clears it below), on the grounds that
         * revealing shows an unpositioned thread. REJECTED, and the test
         * "shows the thread when the opening scroll is cancelled by a
         * prepend, instead of leaving it hidden" is the record: AC 21 fails
         * TOWARDS VISIBLE by design ("unscrolled and legible beats invisible
         * and correct"), and a prepend means the reader is deliberately in
         * history, where the bottom is not where they want to be anyway.
         */
        reveal();
        return;
      }
      /**
       * ⚠️ THE OPENING SCROLL HOLDS UNTIL THE THREAD SETTLES; a later
       * message's does not. `VirtualizedList` renders its rows in BATCHES, so
       * a thread of any length reports its content size several times as the
       * cells below the fold lay out — and a list positioned on the FIRST of
       * those sits at the bottom of a thread that is still growing, which is
       * not the bottom. While opening, every size change re-applies the
       * scroll; the settle callback below ends the window. An append
       * (`'animated'`) fires exactly once: the reader is already reading, and
       * a second yank would be the panel moving under them.
       */
      if (mode === 'animated') {
        followEnd.current = false;
        scrollToEnd(mode);
        reveal();
        return;
      }
      scrollToEnd(mode);
      /**
       * ⚠️ STILL HIDDEN. Revealing here would show the thread between two of
       * its own size reports, and every later batch would then move it in
       * front of the reader — the web flash. The settle window below waits
       * for the reports to stop; `MESSAGE_LIST_REVEAL_TIMEOUT_MS` caps the
       * whole wait, and neither timer can outlive the component.
       */
      if (settleTimer.current !== null) clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(settleOpening, MESSAGE_LIST_SETTLE_MS);
    },
    [scrollToEnd, reveal, settleOpening],
  );

  /** A new `nonce` is a new jump; the same one on a re-render is nothing. */
  const lastRevealNonce = useRef<number | null>(null);
  useEffect(() => {
    if (revealRequest === null) {
      // A cancel (the Files row collapsed, the thread left) while the jump
      // was still queued behind the opening window: the queue is emptied so
      // `settleOpening` does not run a jump the reader called off (D7;
      // review 2026-09-19).
      pendingReveal.current = null;
      return;
    }
    if (lastRevealNonce.current === revealRequest.nonce) return;
    lastRevealNonce.current = revealRequest.nonce;
    retriedNonce.current = null;
    if (opening.current) {
      pendingReveal.current = revealRequest;
      return;
    }
    performReveal(revealRequest);
  }, [revealRequest, performReveal]);

  const handleScrollToIndexFailed = useCallback(
    (info: {
      index: number;
      highestMeasuredFrameIndex: number;
      averageItemLength: number;
    }) => {
      // The platform's own estimate first, so the reader is at least near.
      listRef.current?.scrollToOffset({
        offset: info.averageItemLength * info.index,
        animated: false,
      });
      const target = currentReveal.current;
      if (target === null || retriedNonce.current === target.nonce) return;
      retriedNonce.current = target.nonce;
      // ONE retry, on the next tick, once the cells the estimate brought into
      // view have reported their layout. A second failure is left there.
      if (retryTimer.current !== null) clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(() => {
        retryTimer.current = null;
        // By id, not by the failed call's index: a page prepended between
        // the failure and now has renumbered every row.
        const index = latestMessages.current.findIndex(
          m => m.id === target.messageId,
        );
        if (index < 0) return;
        listRef.current?.scrollToIndex({
          index,
          animated: !reduceMotion,
          viewPosition: 0.5,
        });
      }, 0);
    },
    [reduceMotion],
  );

  const handleLoadOlder = useCallback(() => {
    if (hasOlder && !loadingOlder) onLoadOlder?.();
  }, [hasOlder, loadingOlder, onLoadOlder]);

  /** See the header: armed by a scroll event, disarmed by a fire. */
  const scrollArmed = useRef(false);
  const handleScroll = useCallback(() => {
    scrollArmed.current = true;
  }, []);
  const handleStartReached = useCallback(() => {
    if (!scrollArmed.current) return;
    scrollArmed.current = false;
    handleLoadOlder();
  }, [handleLoadOlder]);

  const header = hasOlder ? (
    <View style={styles.olderWrap} testID="correspondence-load-older-wrap">
      {loadingOlder ? (
        <ActivityIndicator
          color={colors.primary}
          testID="correspondence-load-older-spinner"
        />
      ) : (
        <TouchableOpacity
          onPress={handleLoadOlder}
          style={styles.olderBtn}
          testID="correspondence-load-older"
          accessibilityRole="button"
          accessibilityLabel={LOAD_OLDER_LABEL}
        >
          <Text style={styles.olderText}>{LOAD_OLDER_LABEL}</Text>
        </TouchableOpacity>
      )}
    </View>
  ) : null;

  const empty =
    loading && messages.length === 0 ? (
      <ActivityIndicator color={colors.primary} />
    ) : null;

  return (
    <FlatList
      ref={listRef}
      style={[
        styles.body,
        !positioned && messages.length > 0 && styles.bodyOpening,
      ]}
      contentContainerStyle={styles.bodyContent}
      data={messages}
      keyExtractor={messageKeyExtractor}
      renderItem={renderItem}
      initialNumToRender={MESSAGE_LIST_INITIAL_RENDER}
      // Reading order puts the newest at the bottom, so a prepended page must
      // not move the row the reader is on. `minIndexForVisible: 0` anchors on
      // the first visible row.
      maintainVisibleContentPosition={{ minIndexForVisible: 0 }}
      onScroll={handleScroll}
      onLayout={handleLayout}
      onContentSizeChange={handleContentSizeChange}
      onStartReached={hasOlder ? handleStartReached : undefined}
      onStartReachedThreshold={0.5}
      // Mandatory beside `scrollToIndex` with no `getItemLayout` (Story 7.6,
      // C5): both platforms invariant on it when the index is unmeasured.
      onScrollToIndexFailed={handleScrollToIndexFailed}
      ListHeaderComponent={header}
      ListFooterComponent={footer}
      ListEmptyComponent={empty}
      testID="correspondence-message-list"
    />
  );
};

const styles = StyleSheet.create({
  /** Painted, measured, not yet shown (AC 21). See `positioned`. */
  bodyOpening: { opacity: 0 },
  body: {
    flex: 1,
    // Web only (P4): a wheel that reaches the end of this list must not chain
    // into the page and scroll the request. Style prop, not a ref effect:
    // react-native-web 0.21.2 supports `overscrollBehavior` outright
    // (expanded to the -x/-y longhands; executed check on `atomic()`).
    ...Platform.select({ web: { overscrollBehavior: 'contain' } as any }),
  },
  /**
   * Since Story 5.5 the list body carries NO `gap`: a group spans several
   * FlatList items, so the space between items is the ITEM's to decide
   * (`MessageRow`: 16 between groups, 4 inside one, 8 under a day pill).
   * A list-level gap would put 16 between two bubbles of one group.
   */
  // Horizontal half is `LIST_PADDING_H`: a term in the inline picture width
  // (#1710, `bubbleGeometry.ts`), so it is read from there, not restated.
  bodyContent: {
    paddingVertical: spacing.md,
    paddingHorizontal: LIST_PADDING_H,
  },
  olderWrap: { alignItems: 'center', paddingVertical: spacing.xs },
  olderBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: spacing.cardBorderRadius,
    borderWidth: 1,
    borderColor: colors.borderPanel,
    backgroundColor: colors.backgroundPrimary,
  },
  /**
   * #1069 Story 8.4 (D6, extended at code review 2026-09-21). The
   * "Load earlier messages" label is a TEXT ACTION, the same kind of control
   * as the team strip's Show/Hide, so it takes `textLink` like every other
   * text action the panel owns. It was `colors.info`, which measured
   * **4.02:1** on `backgroundPrimary` and failed SC 1.4.3; `textLink` measures
   * **8.22:1** there. D6's own wording said "inside a bubble" and this label
   * sits on the panel ground rather than in a bubble, so it is a deliberate
   * scope extension: the defect class D6 exists to end is `info` as READABLE
   * TEXT anywhere in the thread, and leaving one failing instance behind would
   * have made the records' claim ("`info` survives in the thread only as a
   * shape") false.
   */
  olderText: {
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.semibold,
    color: colors.textLink,
  },

  /**
   * Pending rows take `MessageRow`'s OWN-SIDE GROUP shape (Story 5.5, C4),
   * muted: `You` above, the own radii, no avatar. The footer keeps its own
   * spacing because the list body no longer has a gap.
   */
  pendingList: { gap: spacing.xs, paddingTop: spacing.lg },
  pendingRow: { flexDirection: 'row', alignItems: 'flex-start' },
  pendingSpacer: { flex: 1, minWidth: spacing.xl },
  pendingColumn: {
    flexShrink: 1,
    maxWidth: '85%',
    gap: spacing.xs,
    alignItems: 'flex-end',
  },
  pendingAuthor: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textSecondary,
    textAlign: 'right',
    paddingHorizontal: spacing.xs,
  },
  /**
   * The OWN fill, the own shape, and the only border left in the thread
   * (#1069 Story 8.4, D2 + D3, owner and UX 2026-09-21). This is the reader's
   * own message that has not been sent yet, so it takes `MessageRow`'s
   * own-side fill and `BUBBLE_RADIUS` with the 4 tail; "not yet" is the
   * dashed `borderPanel` edge and the italic `Sending…` line where a
   * timestamp would stand, both structural, plus its place in the footer
   * after every live row.
   *
   * ⚠️ THE OLD POSITION, AND WHY IT WENT. Story 5.1 D1 (kept by 5.5) said
   * "DELIBERATELY NOT `surfaceMessage`": with every live bubble bordered, a
   * dashed border and a 0.7 opacity were "too little" to tell "still trying"
   * from "delivered", so the fill was muted. Measured for 8.4, the opacity
   * put `Sending…` at 2.82:1 and Cancel at 2.47:1 (the whole group
   * composited over white), both under SC 1.4.3; and once live bubbles lost
   * their border (D2) the dashed edge became a STRONGER "not yet" signal
   * than it was, not a weaker one. So: own fill, no opacity, the border kept.
   *
   * ⚠️ WHETHER THE DASHES ACTUALLY DRAW IS UNVERIFIED UNTIL THE PLATFORM
   * WALK (corrected at code review, 2026-09-21). A uniform `borderWidth` is
   * one precondition, but the known React Native hazard is different and
   * this style meets it: `borderStyle: 'dashed'` is documented to degrade to
   * a SOLID edge on Android (and has been seen to on iOS) when a CORNER
   * RADIUS is set, and this style sets `borderRadius: BUBBLE_RADIUS` plus
   * `borderBottomRightRadius`. Nothing in a unit test can see it -- RNTL
   * reads the style object, not a rasterised edge. If the walk finds a solid
   * edge, the pending bubble has lost its ONLY non-textual cue (it shares the
   * own fill since D3 and no longer has the 0.7 opacity) and the story must
   * add one; that is a named FAIL condition in Task 10's rows 7 and 8.
   */
  pendingBubble: {
    gap: spacing.xs,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    borderRadius: BUBBLE_RADIUS,
    borderBottomRightRadius: spacing.xs,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.borderPanel,
    backgroundColor: colors.surfaceMessage,
  },
  // `textSecondary`, not `textMuted`: no text in the thread sits on
  // `textMuted` after Story 5.5 (D3; the `colors.test.ts` exception shrank).
  pendingStatus: {
    fontSize: 10,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  /** AC 8: the 2px determinate bar and its caption, under `Sending…`. */
  pendingProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  pendingProgressTrack: {
    flex: 1,
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.borderPanelSoft,
    overflow: 'hidden',
  },
  pendingProgressFill: {
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.info,
  },
  pendingCaption: {
    fontSize: 10,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  pendingCancel: {
    fontSize: 10,
    // Story 8.4 C2/D6: `info` here measured 2.47:1 under the old opacity
    // and would have been 3.29:1 on the own fill; `textLink` is 6.73:1.
    color: colors.textLink,
    marginTop: spacing.xs,
  },
});
